import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faPen,
  faScaleBalanced,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import HollowButton from "components/HollowButton";
import ConfirmModal from "components/ConfirmModal";

import councilService from "api/councilService";
import { ICouncilAgendaAttachment, ICouncilAgendaItem } from "dto/Council/ICouncil";
import { ENotificationType, notification } from "utils/notification";

import AttachmentTitleModal from "./AttachmentTitleModal";
import styles from "./styles.module.scss";

interface Props {
  item: ICouncilAgendaItem;
  /** Porządek zamknięty do edycji (sesja zakończona/zamknięta) — tylko podgląd. */
  readOnly: boolean;
  onClose: () => void;
  /** Zmiana listy załączników punktu — rodzic zapisuje porządek. */
  onChange: (attachments: ICouncilAgendaAttachment[]) => void;
}

// Lista załączników punktu porządku obrad — odwzorowanie AttachmentsListModal
// z Magistratu (header „Lista załączników", wiersz = tytuł + nazwa pliku, akcja „Usuń").
//
// Świadome różnice względem oryginału:
//  * usunięcie wymaga potwierdzenia (w Magistracie kasowało od razu — defekt D6),
//  * da się zmienić tytuł istniejącego załącznika (w Magistracie tytuł nadawało się
//    tylko raz, przy dodawaniu — luka D18),
//  * wiersz pokazuje zapisany tytuł, a nie dane runtime, więc nie potrafi być pusty (D5).

const formatSize = (bytes: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentsListModal = ({ item, readOnly, onClose, onChange }: Props) => {
  const [toRemove, setToRemove] = useState<ICouncilAgendaAttachment | null>(null);
  const [toRename, setToRename] = useState<ICouncilAgendaAttachment | null>(null);

  const { mutate: getDownload } = councilService.useGetAttachmentDownload();

  const attachments = item.Attachments ?? [];

  const download = (attachment: ICouncilAgendaAttachment) => {
    // Załącznik zapisany dopiero lokalnie (porządek jeszcze nieutrwalony) nie ma
    // jeszcze serwerowego Id, więc nie da się po niego sięgnąć.
    getDownload(attachment.Id, {
      onSuccess: (res) => {
        if (res.data.Url) window.open(res.data.Url, "_blank", "noopener");
      },
      onError: () =>
        notification({
          type: ENotificationType.ERROR,
          title: "Błąd",
          text: "Nie udało się pobrać załącznika. Zapisz porządek obrad i spróbuj ponownie.",
        }),
    });
  };

  const applyRename = (title: string) => {
    if (!toRename) return;

    onChange(
      attachments.map((a) => (a.Id === toRename.Id ? { ...a, Title: title } : a)),
    );
    setToRename(null);
  };

  const applyRemove = () => {
    if (!toRemove) return;

    onChange(attachments.filter((a) => a.Id !== toRemove.Id));
    setToRemove(null);
  };

  return (
    <>
      <div className={styles.overlay} onMouseDown={onClose}>
        <div
          className={`${styles.modal} ${styles.modalWide}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className={styles.modalTitle}>Lista załączników</div>

          <div className={styles.modalBody}>
            {!attachments.length && (
              <div className={styles.emptyAttachments}>Brak załączników</div>
            )}

            {attachments.map((attachment) => (
              <div key={attachment.Id} className={styles.attachmentRow}>
                <div className={styles.attachmentInfo}>
                  <div className={styles.attachmentTitle}>
                    {attachment.Title || attachment.FileName}
                  </div>
                  <div className={styles.attachmentFile}>
                    {attachment.LegalActId
                      ? [attachment.ActTypeName, attachment.ActNumber]
                          .filter(Boolean)
                          .join(" Nr ")
                      : attachment.FileName}
                    {!attachment.LegalActId && attachment.Size
                      ? ` · ${formatSize(attachment.Size)}`
                      : ""}
                  </div>
                </div>

                {attachment.LegalActId ? (
                  // Akt jest RELACJĄ, nie plikiem — nie ma czego pobierać ani jak
                  // nazwać; tytuł niesie metryka aktu z modułu prawa miejscowego.
                  <span className={styles.attachmentFile} title="Projekt uchwały">
                    <FontAwesomeIcon icon={faScaleBalanced} />
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Pobierz"
                    onClick={() => download(attachment)}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                )}

                {!readOnly && (
                  <>
                    {!attachment.LegalActId && (
                      <button
                        type="button"
                        className={styles.toolBtn}
                        title="Zmień tytuł"
                        onClick={() => setToRename(attachment)}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                    )}
                    <button
                      type="button"
                      className={`${styles.toolBtn} ${styles.toolBtnDanger}`}
                      title="Usuń"
                      onClick={() => setToRemove(attachment)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className={styles.modalFooter}>
            <HollowButton text="Zamknij" onClick={onClose} />
          </div>
        </div>
      </div>

      {toRename && (
        <AttachmentTitleModal
          header="Tytuł załącznika"
          confirmLabel="Zapisz"
          initialTitle={toRename.Title ?? toRename.FileName ?? ""}
          fileName={toRename.FileName}
          onClose={() => setToRename(null)}
          onApply={applyRename}
        />
      )}

      {toRemove && (
        <ConfirmModal
          title="Usunąć?"
          text={`Czy na pewno chcesz usunąć załącznik „${
            toRemove.Title || toRemove.FileName
          }"?`}
          acceptLabel="Usuń"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={applyRemove}
          onCancel={() => setToRemove(null)}
        />
      )}
    </>
  );
};

export default AttachmentsListModal;
